import React from 'react';
import { Button, IconButton, Box } from '@mui/material';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import UnitPageShell from '../../components/shared/UnitPageShell';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import ZoomableImage from '../../components/shared/ZoomableImage';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { useUnitOverride, uploadUnitAsset, updateUnitContent } from '../../services/unitOverridesService';

const PAGE_ID = 'm3_u1_x2_wp';

const images = [
  { key: 'frost', src: '/assets/drawable/frost.jpeg', alt: 'Frost Symbol', label: 'Frost' }
];

const textBlocks = [
  {
    id: 'block_titel',
    heading: 'Kompressionskältemaschinen & Wärmepumpen',
    body: 'Das Herzstück aktiver Kälte- und Wärmeerzeugung'
  },
  {
    id: 'block_intro',
    heading: 'Einführung',
    body:
      'Kompressionskältemaschinen sind die Arbeitstiere, wenn es darum geht, aktiv Kälte zu erzeugen. Das gleiche Prinzip, nur umgekehrt, nutzen Wärmepumpen, ' +
      'um Wärme aus einer Quelle niedrigerer Temperatur auf ein höheres Temperaturniveau zu "pumpen". Wir schauen uns an, wie sie funktionieren und wie sie intelligent gesteuert werden.'
  },
  {
    id: 'block_funktionsprinzip',
    heading: 'Funktionsprinzip: Der Kältekreislauf',
    body:
      'Der Prozess basiert auf einem geschlossenen Kreislauf mit einem Kältemittel, das vier Hauptkomponenten durchläuft:\n\n' +
      '1. **Verdampfer:** Das flüssige Kältemittel nimmt Wärme aus der zu kühlenden Umgebung auf (z.B. Raumluft, Wasser) und verdampft bei niedrigem Druck und niedriger Temperatur.\n\n' +
      '2. **Verdichter (Kompressor):** Das gasförmige Kältemittel wird angesaugt und stark verdichtet. Dabei steigen Druck und Temperatur des Kältemittels deutlich an.\n\n' +
      '3. **Verflüssiger (Kondensator):** Das heiße, gasförmige Kältemittel gibt Wärme an die Umgebung ab (z.B. Außenluft, Heizungswasser) und kondensiert wieder zu Flüssigkeit – bei hohem Druck.\n\n' +
      '4. **Expansionsventil (Drossel):** Das flüssige Kältemittel wird entspannt, wodurch Druck und Temperatur stark absinken. Es ist nun wieder bereit, im Verdampfer Wärme aufzunehmen.\n\n' +
      'Bei einer **Wärmepumpe** wird die im Verflüssiger abgegebene Wärme zum Heizen genutzt. Die im Verdampfer aufgenommene Wärme stammt dann z.B. aus der Außenluft, dem Erdreich oder Grundwasser.'
  },
  {
    id: 'block_glt',
    heading: 'Anbindung an die Gebäudeleittechnik (GLT)',
    body:
      'Moderne Kompressionskältemaschinen und Wärmepumpen verfügen über Schnittstellen zur Integration in eine Gebäudeleittechnik (GLT). ' +
      'Dies ermöglicht eine zentrale Überwachung, Steuerung und Optimierung des Betriebs.'
  },
  {
    id: 'block_bussysteme',
    heading: '1. Bussysteme (Digitale Kommunikation)',
    body:
      'Die Anbindung über Bussysteme ist die modernste und flexibelste Methode. Sie ermöglicht den Austausch einer Vielzahl von Datenpunkten ' +
      '(Temperaturen, Drücke, Betriebszustände, Sollwerte, Fehlermeldungen etc.) über ein standardisiertes Protokoll.\n\n' +
      '**Vorteile:**\n' +
      '• Umfangreicher Datenaustausch\n' +
      '• Detaillierte Diagnosemöglichkeiten\n' +
      '• Fernparametrierung und -steuerung\n' +
      '• Weniger Verkabelungsaufwand im Vergleich zu vielen einzelnen Kontakten\n\n' +
      '**Gängige Protokolle:**\n' +
      '• **Modbus (RTU/TCP):** Weit verbreitet, relativ einfach zu implementieren.\n' +
      '• **BACnet (MS/TP / IP):** Speziell für die Gebäudeautomation entwickelt, sehr umfangreich.\n' +
      '• **KNX:** Vor allem im Bereich der Haus- und Raumautomation, kann aber auch für HLK-Geräte genutzt werden.\n' +
      '• **LON:** Ein weiteres etabliertes Protokoll in der Gebäudeautomation.\n' +
      '• Herstellerspezifische Protokolle (oft mit Gateways zu Standardprotokollen).\n\n' +
      '**Realisierung:** Die Kältemaschine/Wärmepumpe verfügt über eine entsprechende Bus-Schnittstellenkarte. ' +
      'Die GLT-Zentrale (DDC-Controller) hat ebenfalls eine passende Schnittstelle oder ein Gateway. ' +
      'Die Datenpunkte werden in der GLT projektiert und können dann für Regelungsalgorithmen, Visualisierungen und Alarmierungen verwendet werden.'
  },
  {
    id: 'block_physikalisch',
    heading: '2. Physikalische Kontakte (Konventionelle Anbindung)',
    body:
      'Diese Methode nutzt direkte elektrische Verbindungen für grundlegende Steuerungs- und Meldefunktionen.\n\n' +
      '**Typische Signale:**\n' +
      '• **Digitale Eingänge an der Maschine (von GLT gesteuert):**\n' +
      '  - Freigabe Ein/Aus (potenzialfreier Kontakt)\n' +
      '  - Umschaltung Kühlen/Heizen (bei Wärmepumpen)\n' +
      '  - Sollwertvorgabe über Stufen (z.B. 2-3 feste Sollwerte)\n' +
      '• **Digitale Ausgänge von der Maschine (an GLT gemeldet):**\n' +
      '  - Betriebsmeldung (Maschine läuft)\n' +
      '  - Sammelstörmeldung (Fehler liegt an)\n' +
      '  - Meldung Abtauen (bei Luft-Wasser-Wärmepumpen)\n' +
      '• **Analoge Eingänge an der Maschine (von GLT gesteuert):**\n' +
      '  - Externer Sollwert (z.B. 0-10V Signal für Temperatur oder Leistung)\n' +
      '• **Analoge Ausgänge von der Maschine (an GLT gemeldet):**\n' +
      '  - Ist-Temperatur (z.B. Vorlauftemperatur als 0-10V Signal)\n\n' +
      '**Vorteile:**\n' +
      '• Einfache Implementierung für grundlegende Funktionen.\n' +
      '• Oft als Rückfallebene oder bei einfacheren Anlagen.\n\n' +
      '**Nachteile:**\n' +
      '• Begrenzte Anzahl an Informationen.\n' +
      '• Hoher Verkabelungsaufwand bei vielen Signalen.\n' +
      '• Keine detaillierte Diagnose oder Fernparametrierung möglich.'
  },
  {
    id: 'block_fazit',
    heading: 'Fazit',
    body:
      'Kompressionskältemaschinen und Wärmepumpen sind Schlüsseltechnologien für die moderne Gebäudeklimatisierung. ' +
      'Ihre effiziente Regelung und die Integration in übergeordnete Gebäudeleittechniksysteme über Busprotokolle oder physikalische Kontakte sind entscheidend für einen energiesparenden und bedarfsgerechten Betrieb.'
  }
];

const M3_U1_X2_WP: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM3, canUploadImagesM3 } = useAuth();
  const canEditTexts = canEditTextsM3;
  const canUploadImages = canUploadImagesM3;
  const { override, refresh } = useUnitOverride(PAGE_ID);

  const imgSrc = (key: string, fallback: string) => {
    const v = override?.images?.[key];
    if (!v) return fallback;
    const s = String(v);
    return s.startsWith('/assets/') || s.startsWith('assets/') ? s : `/assets/unit_custom/${s}`;
  };

  const mergedBlocks = React.useMemo(() => 
    textBlocks.map(tb => ({
      ...tb,
      body: override?.texts?.[tb.id] || tb.body
    })),
    [override]
  );

  const fullText = React.useMemo(() => mergedBlocks.map(b => `${b.heading}. ${b.body}`).join(' '), [mergedBlocks]);
  const { speak, stop, speaking } = useTTS({ text: fullText });

  const handleTextSave = React.useCallback(async (blockId: string, body: string) => {
    if (!token) return alert('Kein Token.');
    if (!canEditTexts) return alert('Keine Berechtigung zum Texte bearbeiten.');
    try {
      await updateUnitContent(PAGE_ID, { texts: { [blockId]: body } }, token);
      alert('Text gespeichert.');
      await refresh();
    } catch (e: any) {
      alert('Speichern fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh, canEditTexts]);

  const handleImageReplace = React.useCallback(async (key: string, file: File) => {
    if (!token) return alert('Kein Token.');
    if (!canUploadImages) return alert('Keine Berechtigung fuer Bilder-Uploads.');
    try {
      await uploadUnitAsset(PAGE_ID, key, file, token);
      alert('Bild aktualisiert.');
      await refresh();
    } catch (e: any) {
      alert('Upload fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh, canUploadImages]);

  const ttsBtn = React.createElement(IconButton, { onClick: speaking ? stop : speak, 'aria-label': 'Vorlesen', sx: { ml: 1 } }, React.createElement(SpeakerIcon, { sx: { fontSize: 28 } }));

  return React.createElement(UnitPageShell, { title: '3.1.2 Kompressionskälte & Wärmepumpen', actions: ttsBtn },
    React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' }, gap: 2 } },
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        images.map(img => React.createElement(ZoomableImage, {
          key: img.key,
          src: imgSrc(img.key, img.src),
          alt: img.alt,
          label: 'Bild einfügen',
          onReplace: canUploadImages ? (file: File) => handleImageReplace(img.key, file) : undefined
        }))
      ),
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        mergedBlocks.map(tb => React.createElement(EditableTextBlock, {
          key: tb.id,
          pageId: PAGE_ID,
          blockId: tb.id,
          heading: tb.heading,
          body: tb.body,
          onSave: (body: string) => handleTextSave(tb.id, body)
        })),
        React.createElement(Box, { sx: { display: 'flex', gap: 1, mt: 2 } },
          React.createElement(Button, { variant: 'outlined', onClick: () => navigate('/units/m3_u1_x99_quiz') }, 'Zum Quiz')
        )
      )
    ),
    React.createElement(Box, { sx: { display: 'flex', gap: 1, mt: 2 } })
  );
};

export default M3_U1_X2_WP;

